import { Button, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { columns } from './saved-query-column';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { Operator } from '../../../models/query-builder';
import { queryBuilderApiKeyStore } from '../../../api/query-builder-api-key-store';
import { useDeleteData } from '../../../hooks/useDeleteData';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GridColumnConfig, GridPreference } from '../../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../../api/grid-api-key-store';
import { useSaveData } from '../../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../hooks/use-debounce';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { SaveHookFormDialog } from '../../../components/save-hook-form.dialog';
import { ScheduleEmailDialog } from './schedule-email/schedule-email.dialog';
import { useAuthorize } from '../../../hooks/use-authorize';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

interface CellProps {
  queryData: any;
}

interface CustomGridCellProps {
  field: string;
}

const SavedQueryPage = () => {
  const {
    data,
    isLoading: isLoadingOperator,
    refetch,
  } = useLoadData<ApiResponse<any[]>>(queryBuilderApiKeyStore.getAllReports());
  const [currentReportId, setCurrentReportId] = useState(0);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isScheduleEmail, setIsScheduleEmail] = useState(false);
  const isScheduleQueryEnabled = process.env.REACT_APP_SCHEDULE_QUERY_FLAG === 'true';

  const {
    isLoading: isloadingDelete,
    isError,
    error,
    mutateAsync: deleteReport,
  } = useDeleteData(queryBuilderApiKeyStore.deleteReport(currentReportId || 0));

  useEffect(() => {
    navigate('/custom-builder?tab=saved_query');
  }, [location]);

  const handleRunQuery = (selectedReportName: any, reportId: any) => {
    const encodedReportName = !selectedReportName ? selectedReportName : encodeURIComponent(selectedReportName);
    const queryParams = new URLSearchParams({
      reportId: reportId,
    }).toString();
    // const encryptedParam = encryptWithAES(JSON.stringify(selectedReportName));
    window.open(`/custom-builder/execute-report/${encodedReportName}?${queryParams}`, '_blank');
  };

  const openScheduleEmailDialog = (id: any) => {
    setIsScheduleEmail(true);
    setCurrentReportId(id);
  };

  const openDialog = (id: any) => {
    setIsOpen(true);
    setCurrentReportId(id);
  };

  const handleEditQueryBuilder = (id: any) => {
    navigate(`/custom-builder/edit/${id}?tab=custom_query`);
  };

  const EditCell = ({ queryData }: CellProps) => {
    const [hovered, setHovered] = useState(false);
    const handleMouseOver = () => {
      setHovered(true);
    };
    const handleMouseOut = () => {
      setHovered(false);
    };
    const buttonStyle = {
      color: 'white',
      backgroundColor: hovered ? '#0000b5' : '#0000b5',
      padding: '8px',
      maxHeight: '28px',
    };

    return (
      <td style={{ display: 'flex' }}>
        <GuardedComponent requiredPermissions={[UserPermissionName.RUN_QUERY_CUSTOM_REPORT]}>
          <Button
            className="btn  btn-primary btn-sm sitDefaultBtn"
            size="small"
            style={buttonStyle}
            disabled={isReadOnlyPermission}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            onClick={() => {
              handleRunQuery(queryData.reportName, queryData?.reportId);
            }}
          >
            RUN
          </Button>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_QUERY_CUSTOM_REPORT]}>
          <IconButton
            //  disabled={!bundleService?.isEditable}
            aria-label="edit"
            onClick={() => handleEditQueryBuilder(queryData?.reportId)}
          >
            <Tooltip title={<Typography variant="body1">Edit</Typography>}>
              <EditIcon className="btnEdit" />
            </Tooltip>
          </IconButton>
        </GuardedComponent>
        <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_QUERY_CUSTOM_REPORT]}>
          <IconButton style={{ padding: 5 }} aria-label="delete" onClick={() => openDialog(queryData?.reportId)}>
            <Tooltip title={<Typography variant="body1">Delete</Typography>}>
              <DeleteIcon className="btnDelete" />
            </Tooltip>
          </IconButton>
        </GuardedComponent>
        {isScheduleQueryEnabled && (
          <IconButton
            style={{ padding: 5 }}
            aria-label="schedule"
            onClick={() => openScheduleEmailDialog(queryData?.reportId)}
          >
            <Tooltip title={<Typography variant="body1">Schedule Query</Typography>}>
              <AccessTimeIcon className="btnEdit" />
            </Tooltip>
          </IconButton>
        )}
      </td>
    );
  };

  const submitHanlder = async () => {
    await deleteReport().then(() => {
      setIsOpen(false);
      setCurrentReportId(0);
      refetch();
    });
  };

  const SavedQueryCustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'option') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <EditCell queryData={dataItem.dataItem} />;
      };
    }
  };

  const closedScheduleEmailDialog = () => {
    setIsScheduleEmail(false);
    setCurrentReportId(0);
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('SavedCustomQuery', 'SavedCustomQuery'));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredSavedCustomQuries, setFilteredSavedCustomQuries] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredSavedCustomQueryList = data?.data?.filter((SavedCustomQuery) =>
      Object.values(SavedCustomQuery).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredSavedCustomQuries(filteredSavedCustomQueryList || []);
  }, [debouncedSearchTerm, data?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'SavedCustomQuery',
      pageName: 'SavedCustomQuery',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'SavedCustomQuery',
      pageName: 'SavedCustomQuery',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <div>
      <Paper elevation={0} style={styling.paperStyle}>
        {isLoadingOperator && <LoadingIndicator isLoading={isLoadingOperator} />}
        {data?.data && (
          <SiTrackGrid
            fileTitle='query_builder'
            columns={columns}
            dataItemKey="reportId"
            customCell={(field) =>
              SavedQueryCustomGridCell({
                field,
              })
            }
            isBusy={isLoadingGridLayout}
            //   viewRoute={(value) => `details/${value.projectID}`}
            // onEdit={handleEditQueryBuilder}
            //   onEdit={form.getValues('status') !== 'archived' ? handleEditProject : undefined}
            //   actionPermissions={{ edit: UserPermissionName.EDIT_PROJECT, export: UserPermissionName.DOWNLOAD_GRID }}

            // -----------------Grid Layout-------
            dataItems={filteredSavedCustomQuries || []}
            reorderable
            handleSearch={handleInputChange}
            handleSaveLayout={handleSaveLayout}
            isSaveLayout={true}
            gridConfigFromApi={gridConfig}
            handleResetLayout={handleResetLayout}
          // -----------------Grid Layout-------
          />
        )}
      </Paper>
      {isOpen && (
        <Dialog className="modalpopup" title={'CONFIRM DELETE'} onClose={() => setIsOpen(false)}>
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>Are you sure you want to permanently delete?</div>
              <LoadingIndicator isLoading={isloadingDelete} />
            </div>
          </div>{' '}
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button className="btn btn-sm btn-default btn-lightGrey" onClick={() => setIsOpen(false)} autoFocus>
                Cancel
              </button>
              <button
                className="btn btn-sm btn-danger btn-delete"
                type="button"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                  color: isReadOnlyPermission ? 'black' : '#fff'
                }}
                onClick={submitHanlder}
                disabled={isloadingDelete || isReadOnlyPermission}
              >
                Delete
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
      {isScheduleEmail && <ScheduleEmailDialog reportID={currentReportId} onClosed={closedScheduleEmailDialog} />}
    </div>
  );
};

export default SavedQueryPage;
